import type { ContractDetails } from "@App/types";
import { createSlice, type PayloadAction } from "@reduxjs/toolkit";

export interface PickUpAddress {
  name: string | null;
  street: string | null;
  postcode: string | null;
  city: string | null;
  phone: string | null;
  email: string | null;
}

export interface DesiredReturnDates {
  date1: string | null;
  date2: string | null;
  date3: string | null;
}

interface VehicleReturnState {
  pickUpAddress: PickUpAddress;
  desiredReturnDates: DesiredReturnDates;
  contractDetails: ContractDetails;
  isAuthenticated: boolean;
}

const initialState: VehicleReturnState = {
  pickUpAddress: {
    name: null,
    street: null,
    postcode: null,
    city: null,
    phone: null,
    email: null,
  },
  desiredReturnDates: {
    date1: null,
    date2: null,
    date3: null,
  },
  contractDetails: {
    carId: null,
    agreementNumber: null,
    name: null,
    street: null,
    postcode: null,
    city: null,
    phone: null,
    contractEndDate: null,
  },
  isAuthenticated: false,
};

const vehicleReturnSlice = createSlice({
  name: "vehicleReturnPickup",
  initialState,
  reducers: {
    setDesiredReturnDates: (
      state,
      action: PayloadAction<{
        date1: Date | null;
        date2: Date | null;
        date3: Date | null;
      } | null>,
    ) => {
      if (!action.payload) {
        state.desiredReturnDates = {
          date1: null,
          date2: null,
          date3: null,
        };
        return;
      }

      state.desiredReturnDates = {
        date1: action.payload.date1 ? action.payload.date1.toISOString() : null,
        date2: action.payload.date2 ? action.payload.date2.toISOString() : null,
        date3: action.payload.date3 ? action.payload.date3.toISOString() : null,
      };
    },
    setPickUpAddress: (state, action: PayloadAction<PickUpAddress | null>) => {
      if (!action.payload) {
        state.pickUpAddress = {
          name: null,
          street: null,
          postcode: null,
          city: null,
          phone: null,
          email: null,
        };
        return;
      }

      state.pickUpAddress = {
        name: action.payload.name,
        street: action.payload.street,
        postcode: action.payload.postcode,
        city: action.payload.city,
        phone: action.payload.phone,
        email: action.payload.email,
      };
    },
    setContractDetails: (state, action: PayloadAction<ContractDetails | null>) => {
      if (!action.payload) {
        state.contractDetails = {
          carId: null,
          name: null,
          street: null,
          postcode: null,
          city: null,
          phone: null,
          agreementNumber: null,
        };
        return;
      }
      state.contractDetails = { ...action.payload };
    },
    setIsAuthenticated: (state, action: PayloadAction<boolean>) => {
      if (!action.payload) return;
      state.isAuthenticated = true;
    },
  },
});

export const { setDesiredReturnDates, setPickUpAddress, setContractDetails, setIsAuthenticated } =
  vehicleReturnSlice.actions;

export default vehicleReturnSlice.reducer;
